/**
 * Container Summary Component
 *
 * Displays a compact summary of the customer's existing container
 * with status and location information.
 */

import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Package2Icon, MapPinIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ContainerInfo {
  orderId?: string;
  containerSize?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  status?: 'active' | 'scheduled' | 'full' | 'partial' | 'empty';
  deliveryDate?: string;
  materialType?: string;
}

interface ContainerSummaryProps {
  orderId?: string;
  containerInfo?: ContainerInfo;
  className?: string;
  loading?: boolean;
}

const STATUS_CONFIG = {
  active: { label: 'Active', variant: 'success' as const },
  scheduled: { label: 'Scheduled', variant: 'secondary' as const },
  full: { label: 'Full', variant: 'warning' as const },
  partial: { label: 'Partial', variant: 'secondary' as const },
  empty: { label: 'Empty', variant: 'outline' as const },
};

export function ContainerSummary({
  orderId,
  containerInfo,
  className,
  loading = false,
}: ContainerSummaryProps) {
  // Mock data for development - this would be replaced with real data fetching
  const mockInfo: ContainerInfo = {
    orderId: orderId || '12345',
    containerSize: '20Y',
    address: {
      street: '123 Main St',
      city: 'San Luis Obispo',
      state: 'CA',
      zipCode: '93401',
    },
    status: 'active',
    deliveryDate: '2025-10-01',
    materialType: 'Mixed C&D',
  };

  const info = containerInfo || mockInfo;
  const statusConfig = info.status ? STATUS_CONFIG[info.status] : null;

  if (loading) {
    return (
      <Card className={cn('animate-pulse', className)}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gray-200 rounded" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/2" />
              <div className="h-3 bg-gray-200 rounded w-3/4" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('bg-blue-50 border-blue-200', className)}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
            <Package2Icon className="w-6 h-6 text-blue-600" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="text-sm font-semibold text-blue-900">
                  Your Current Container
                </h3>
                {info.orderId && (
                  <p className="text-xs text-blue-700">Order #{info.orderId}</p>
                )}
              </div>
              {statusConfig && (
                <Badge variant={statusConfig.variant} className="flex-shrink-0">
                  {statusConfig.label}
                </Badge>
              )}
            </div>

            {/* Container details */}
            <div className="space-y-1 text-sm text-blue-800">
              {info.containerSize && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{info.containerSize} Container</span>
                  {info.materialType && (
                    <>
                      <span className="text-blue-400">•</span>
                      <span>{info.materialType}</span>
                    </>
                  )}
                </div>
              )}

              {/* Address */}
              {info.address && (
                <div className="flex items-start gap-2">
                  <MapPinIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                  <span className="text-xs">
                    {info.address.street}
                    {info.address.city && `, ${info.address.city}`}
                    {info.address.state && `, ${info.address.state}`}
                    {info.address.zipCode && ` ${info.address.zipCode}`}
                  </span>
                </div>
              )}

              {/* Delivery date */}
              {info.deliveryDate && (
                <div className="text-xs text-blue-700">
                  Delivered: {new Date(info.deliveryDate).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
